import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import api, { API, money } from "@/lib/api";
import { Download, Package, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (ready && !user) navigate("/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  const download = async (orderId, slug, name, book) => {
    const bid = orderId + slug + (book || "");
    setBusy(bid);
    try {
      const res = await api.get(`/downloads/${orderId}/${slug}${book ? `?book=${book}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${book || slug}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Downloading ${name}`);
    } catch (e) {
      toast.error("Download failed");
    } finally { setBusy(""); }
  };

  if (!ready || !user) return null;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
      <Seo title="My Account" path="/account" noindex />
      <p className="font-mono-label text-xs text-primary mb-3">Account</p>
      <h1 className="font-display text-4xl sm:text-5xl font-light mb-2">Hi, {user.name}</h1>
      <p className="text-muted-foreground mb-12 font-mono text-sm">{user.email}</p>

      <h2 className="font-display text-2xl mb-6 flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Order History</h2>

      {orders === null ? (
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      ) : orders.length === 0 ? (
        <div className="border border-border p-10 text-center">
          <p className="text-muted-foreground mb-4">No orders yet.</p>
          <Link to="/shop" className="font-mono-label text-xs text-primary hover:underline">Start shopping →</Link>
        </div>
      ) : (
        <div className="space-y-6" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="border border-border bg-card" data-testid={`order-${o.order_id}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <span className="font-mono text-xs text-muted-foreground">#{o.order_id.slice(0, 8).toUpperCase()}</span>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span>
                  <span className="font-mono text-sm font-semibold">{money(o.amount)}</span>
                  <span className="font-mono-label text-[10px] px-2 py-1 bg-primary/15 text-primary border border-primary/30">PAID</span>
                </div>
              </div>
              <div className="divide-y divide-border">
                {o.items.map((it) => (
                  <div key={it.slug} className="flex items-start gap-4 px-5 py-4">
                    <img src={it.image} alt={it.name} className="w-14 h-14 object-cover border border-border shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg truncate">{it.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">Qty {it.quantity} · {money(it.price)}</p>
                      <Link to={`/product/${it.slug}#reviews`} data-testid={`review-cta-${it.slug}`}
                        className="inline-flex items-center gap-1.5 mt-1.5 font-mono-label text-[10px] text-primary hover:underline">
                        <Star className="w-3 h-3" /> Leave a review
                      </Link>
                      {it.type === "digital" && it.downloads?.length > 1 && (
                        <div className="mt-3 space-y-2" data-testid={`order-${o.order_id}-bundle-${it.slug}`}>
                          {it.downloads.map((d) => (
                            <button key={d.key} onClick={() => download(o.order_id, it.slug, d.name, d.book)}
                              disabled={busy === o.order_id + it.slug + (d.book || "")}
                              data-testid={`download-${o.order_id}-${d.key}`}
                              className="w-full sm:w-auto inline-flex items-center gap-2 border border-primary/40 hover:bg-primary/10 text-foreground font-mono-label text-[11px] px-3 py-2 rounded-full transition-colors">
                              {busy === o.order_id + it.slug + (d.book || "") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 text-primary" />}
                              <span className="truncate">{d.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {it.type === "digital" ? (
                      (it.downloads?.length > 1) ? null : (
                        <button onClick={() => download(o.order_id, it.slug, it.name, it.downloads?.[0]?.book)} disabled={busy === o.order_id + it.slug}
                          data-testid={`download-${o.order_id}-${it.slug}`}
                          className="inline-flex items-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-[11px] px-4 py-2.5 rounded-full transition-[filter] shrink-0">
                          {busy === o.order_id + it.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
                        </button>
                      )
                    ) : (
                      <span className="font-mono-label text-[10px] text-muted-foreground shrink-0">Ships to you</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
